import React, { useState } from "react";
import { Clock, Star, Split, Check, X, UserPlus, FileText, MessageCircle, MoreVertical } from "lucide-react";
import { fmt } from "../../../../services/erpApi";
import { AVATAR_BG, getInitials, ODOO, priorityStars, tagDotColor } from "./crmV2Helpers";

const menuItemStyle = (danger) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
  padding: "7px 10px",
  border: "none",
  background: "transparent",
  textAlign: "left",
  cursor: "pointer",
  fontSize: 13,
  fontFamily: "inherit",
  color: danger ? "#be123c" : ODOO.text,
  borderRadius: 4,
});

const CrmV2KanbanCard = ({
  lead,
  stageName,
  userName,
  canManage,
  dragging,
  onDragStart,
  onDragEnd,
  onClick,
  stage,
  onAction,
}) => {
  const filled = priorityStars(lead.priority);
  const tagLabel = stageName || lead.tag_name || (lead.type === "opportunity" ? "Opportunity" : "Lead");
  const company = lead.partner_name || lead.company || lead.commercial_partner_name || "";
  const revenue = Number(lead.expected_revenue) || 0;
  const assignee = userName || lead.user_name || "";
  const [isHovered, setIsHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isTerminal = !!(stage?.is_won || stage?.is_lost);
  const isLead = lead.type === "lead";
  const showKebab = canManage && !isTerminal && !!onAction;

  // --- Odoo-like drag & drop animation ---
  // Hover: grab cursor + deeper shadow (transition)
  // Dragging: rotate 3deg, scale 1.03, large shadow, grabbing, zIndex 999
  // Drop: transition transform 0.2s ease, box-shadow 0.2s ease resets to 0deg / 1
  const handleDragStart = (e) => {
    if (!canManage) return;
    // Create a rotated clone as drag image to reproduce the "feuille attrapée" effect
    // without waiting for the async setState re-render.
    try {
      const el = e.currentTarget;
      const rect = el.getBoundingClientRect();
      const ghost = el.cloneNode(true);
      ghost.style.position = "absolute";
      ghost.style.top = "-1000px";
      ghost.style.left = "-1000px";
      ghost.style.width = `${rect.width}px`;
      ghost.style.margin = "0";
      ghost.style.transform = "rotate(3deg) scale(1.03)";
      ghost.style.boxShadow = "0 12px 28px rgba(0,0,0,0.18), 0 8px 14px rgba(0,0,0,0.12)";
      ghost.style.opacity = "0.95";
      ghost.style.pointerEvents = "none";
      document.body.appendChild(ghost);
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;
      if (e.dataTransfer.setDragImage) e.dataTransfer.setDragImage(ghost, offsetX, offsetY);
      // Cleanup after drag image captured
      setTimeout(() => {
        if (ghost.parentNode) ghost.parentNode.removeChild(ghost);
      }, 0);
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = "move";
        // Types distincts pour différencier drag de carte vs drag de colonne entière
        try { e.dataTransfer.setData("application/x-lead", String(lead.id)); } catch {}
        e.dataTransfer.setData("text/plain", String(lead.id));
      }
    } catch {
      // Fallback: ignore
    }
    if (onDragStart) onDragStart(e, lead);
  };

  const handleDragEnd = (e) => {
    setIsHovered(false);
    if (onDragEnd) onDragEnd(e);
  };

  const closeMenu = () => setMenuOpen(false);
  const handleMenuAction = (action) => {
    closeMenu();
    if (onAction) onAction(action, lead);
  };

  // Shadow definitions
  const baseShadow = "0 1px 2px rgba(0,0,0,0.04), 0 1px 1px rgba(0,0,0,0.03)";
  const hoverShadow = "0 2px 8px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.06)";
  const draggingShadow = "0 12px 28px rgba(0,0,0,0.18), 0 8px 14px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.08)";

  const cardStyle = dragging
    ? {
        background: "#fff",
        border: `1px solid ${ODOO.primary}`,
        borderRadius: 3,
        padding: "10px 12px",
        marginTop: 0,
        marginBottom: 0,
        cursor: "grabbing",
        boxShadow: draggingShadow,
        transform: "rotate(3deg) scale(1.03)",
        willChange: "transform, box-shadow",
        zIndex: 999,
        position: "relative",
        opacity: 0.96,
        transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.15s ease, opacity 0.15s ease",
      }
    : {
        background: "#fff",
        border: `1px solid ${isHovered ? "#c8cfd6" : ODOO.cardBorder}`,
        borderRadius: 3,
        padding: "10px 12px",
        marginTop: 0,
        marginBottom: 0,
        cursor: canManage ? "grab" : "pointer",
        boxShadow: isHovered ? hoverShadow : baseShadow,
        transform: "rotate(0deg) scale(1)",
        willChange: "transform, box-shadow",
        zIndex: 1,
        position: "relative",
        opacity: 1,
        transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.15s ease, opacity 0.15s ease",
      };

  return (
    <div
      data-kanban-card={lead.id}
      draggable={canManage && !menuOpen}
      onDragStart={(e) => { if (menuOpen) { e.preventDefault(); return; } e.stopPropagation(); handleDragStart(e); }}
      onDragEnd={handleDragEnd}
      onMouseEnter={() => !dragging && setIsHovered(true)}
      onMouseLeave={() => { setIsHovered(false); }}
      onClick={onClick}
      style={{ ...cardStyle, zIndex: menuOpen ? 50 : cardStyle.zIndex }}
    >
      {showKebab && (
        <button
          type="button"
          aria-label="Actions"
          onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            top: 6,
            right: 6,
            width: 22,
            height: 22,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            border: "none",
            borderRadius: 4,
            background: menuOpen ? "#f1f5f9" : isHovered ? "#fff" : "transparent",
            color: ODOO.muted,
            cursor: "pointer",
            opacity: menuOpen || isHovered ? 1 : 0,
            pointerEvents: menuOpen || isHovered ? "auto" : "none",
            transition: "opacity .12s ease, background .12s ease",
            zIndex: 2,
          }}
        >
          <MoreVertical size={14} />
        </button>
      )}
      {menuOpen && (
        <>
          <div onClick={(e) => { e.stopPropagation(); closeMenu(); }} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <div
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              position: "absolute",
              top: 28,
              right: 6,
              zIndex: 41,
              minWidth: 160,
              background: "#fff",
              border: `1px solid ${ODOO.border}`,
              borderRadius: 6,
              boxShadow: "0 8px 20px rgba(0,0,0,0.14)",
              padding: 4,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {isLead ? (
              <button type="button" onClick={() => handleMenuAction("convert")} style={menuItemStyle(false)}><Split size={14} style={{ color: "#225f2b" }} /> Convertir</button>
            ) : (
              <>
                <button type="button" onClick={() => handleMenuAction("won")} style={menuItemStyle(false)}><Check size={14} style={{ color: "#225f2b" }} /> Marquer gagné</button>
                <button type="button" onClick={() => handleMenuAction("lost")} style={menuItemStyle(true)}><X size={14} /> Marquer perdu</button>
              </>
            )}
            <div style={{ height: 1, background: ODOO.borderLight, margin: "2px 0" }} />
            <button type="button" onClick={() => handleMenuAction("assign")} style={menuItemStyle(false)}><UserPlus size={14} style={{ color: "#475569" }} /> Assigner</button>
            {lead.type === "opportunity" && (
              <button type="button" onClick={() => handleMenuAction("quotation")} style={menuItemStyle(false)}><FileText size={14} style={{ color: "#0f766e" }} /> Créer devis</button>
            )}
            {(lead.phone || lead.mobile) && (
              <button type="button" onClick={() => handleMenuAction("whatsapp")} style={menuItemStyle(false)}><MessageCircle size={14} style={{ color: "#166534" }} /> WhatsApp</button>
            )}
            <button type="button" onClick={(e) => { e.stopPropagation(); closeMenu(); if (onClick) onClick(); }} style={{ ...menuItemStyle(false), color: ODOO.muted }}>Ouvrir fiche…</button>
          </div>
        </>
      )}
      <div style={{ fontSize: 14, fontWeight: 700, color: ODOO.text, lineHeight: 1.35, wordBreak: "break-word", paddingRight: showKebab ? 20 : 0 }}>
        {lead.name || "—"}
      </div>

      {revenue > 0 && (
        <div style={{ fontSize: 13, fontWeight: 600, color: ODOO.text, marginTop: 4 }}>
          {fmt.money(revenue)}
        </div>
      )}

      {company && (
        <div style={{ fontSize: 12.5, color: ODOO.muted, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {company}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 6, fontSize: 12, color: ODOO.muted }}>
        <span style={{ width: 7, height: 7, borderRadius: "50%", background: tagDotColor(tagLabel), flexShrink: 0 }} />
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tagLabel}</span>
        <span
          style={{
            marginLeft: 6,
            padding: "1px 6px",
            borderRadius: 4,
            fontSize: 9,
            fontWeight: 800,
            letterSpacing: ".03em",
            background: isLead ? "#fffbeb" : "#f0f7f0",
            color: isLead ? "#b45309" : "#225f2b",
            border: `1px solid ${isLead ? "#fde68a" : "#bbf7d0"}`,
            opacity: isHovered || menuOpen ? 1 : 0,
            transform: isHovered || menuOpen ? "translateX(0)" : "translateX(-4px)",
            pointerEvents: "none",
            transition: "opacity .12s ease, transform .12s ease",
            whiteSpace: "nowrap",
          }}
          title={isLead ? "Lead — à convertir" : "Opportunité"}
        >
          {isLead ? "LEAD" : "OPPORTUNITY"}
        </span>
      </div>

      {Array.isArray(lead.group_names) && lead.group_names.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
          {lead.group_names.slice(0, 3).map((name) => (
            <span key={name} style={{ background: "#eef2f7", color: "#334155", borderRadius: 10, padding: "1px 7px", fontSize: 10.5, fontWeight: 600, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={name}>
              {name}
            </span>
          ))}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", marginTop: 10, gap: 6 }}>
        <div style={{ display: "inline-flex", gap: 2 }}>
          {[0, 1, 2].map((i) => (
            <Star
              key={i}
              size={16}
              fill={i < filled ? ODOO.star : "none"}
              color={i < filled ? ODOO.star : "#ced4da"}
              strokeWidth={1.6}
            />
          ))}
        </div>
        {lead.next_activity && (
          <Clock size={13} style={{ color: ODOO.muted, marginLeft: 2 }} title={lead.next_activity.summary || "Activity"} />
        )}
        <div style={{ marginLeft: "auto" }}>
          {assignee ? (
            <span
              title={assignee}
              style={{
                width: 22,
                height: 22,
                borderRadius: "50%",
                background: AVATAR_BG[(Number(lead.user_id) || 0) % AVATAR_BG.length],
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 9,
                fontWeight: 700,
                color: "#fff",
              }}
            >
              {getInitials(assignee)}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default CrmV2KanbanCard;
